import {config, createAppContext, EnforcedLanguageSetting} from './context';
import {Translate} from '@planday-corp/globalization';
import {AppProvider} from '@planday-corp/react-app';
import ToastManager from '@planday-corp/react-app/components/ToastManager';
import ToastProvider from '@planday-corp/react-app/components/ToastProvider';
import CSSReset from '@planday-corp/ui/global/CSSReset';
import Fonts from '@planday-corp/ui/global/Fonts';
import UIProvider from '@planday-corp/ui/global/UIProvider';
import {blue50, grey05, grey50} from '@planday-corp/ui/styles/colors';
import {QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import React, {PropsWithChildren, Suspense, useMemo} from 'react';
import {createGlobalStyle} from 'styled-components';
import {mobiles} from '~/Signup/utils/responsiveness';

// Soft export of types
// It's important that only types are exported to avoid breaking code splitting
export type AppContext = ReturnType<typeof createAppContext>;
export const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';

// This is a workaround specifically for PlandaySignup, not to affect current XeroSignup behavior
// In XeroSignup we're using instance of Api class, but it cannot be instantiated without correct serviceUrl
// In PlandaySignup we need to be able to call BE (self) on any dynamic environment, and not stick to hardcoded staging configuration
// To achieve that we're utilizing fetch API, and calling root path directly
// For full development experience (for .NET devs) frontend should, by default call "http://localhost:5737"

export const SIGNUP_API_URL = IS_DEVELOPMENT
  ? config.get('internalServices.signupApiUrl')
  : '';

const GlobalStyles = createGlobalStyle`
    body, html, #root {
        height: 100%;
        margin: 0;
        overflow-y: auto;
        background-color: ${grey05};
    }

    ${mobiles(`
      .mobileEnabled {
        label > span, button > span {
          font-size: 1rem !important;
        }

        div:has(> input) {
          > div:has(span[class*="InlineSpinner"]) {
            right: 0rem !important;
          }
        }

        input, #phoneCountryCodePrefix {
          height: 3rem !important;
          border-radius: 0.5rem !important;
          padding-right: 2.5rem !important;
          font-size: 1rem !important;
        }

        #phoneCountryCodePrefix {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          margin: 0rem;

          span[class*="InlineSpinner"] {
            right: 2.25rem !important;
          }
        }

        div[role='list'] {
          height: 3rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 0.5rem;
        }

        div[role='checkbox'] svg {
          width: 0.875rem !important;
          height: 0.875rem !important;
        }

        button {
          height: 3rem !important;
          border-radius: 1rem !important;
        }

        svg {
          width: 1.5rem !important;
          height: 1.5rem !important;
        }

        span[class*="InlineSpinner"] {
          width: 1.25rem !important;
          height: 1.25rem !important;
          right: 0.5rem !important;
          top: 0 !important;
          bottom: 0 !important;
          align-self: center !important;
        }
      }

      .fullWidthLabel {
        label  {
          > span {
            max-width: 100vw;
          }
        }
      }

      .pill {
        display: inline-flex;
        align-items: center;
        border-radius: 1rem;
        padding: 0.5rem 1rem;
        margin: 0rem !important;

        svg {
          top: 0;
        }

        span {
          font-size: 0.875rem;
          font-weight: 400;
          color: ${blue50};
        }
      }

      .pillWrapper {
        margin: 0rem !important;
        margin-left: 0.5rem;
        
        > div {
          > input { 
            border-radius: 1rem !important;
            font-size: 0.875rem !important;
            color: ${blue50};
          }
        }
      }

      .formInput {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        margin: 0rem !important;

        div {
          margin: 0rem;
          font-size: 1rem !important;
        }

        svg {
          top: 0;
          bottom: 0;
          align-self: center;
        }

        div:first-of-type:has(> svg, > span[class*="InlineSpinner"]) {
          height: 3rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }

      .hideSvgInLastDiv {
        div:last-of-type {
          svg {
            display: none
          }
        }
      }

      .padMissingLabel {
        padding-top: 0.875rem;
      }

      .legalText {
        font-size: 0.875rem !important;
        font-weight: 400;
        color: ${grey50};
      }
    `)}
`;

interface AppProps {
  enforcedLanguage?: EnforcedLanguageSetting;
}

export default function App(props: PropsWithChildren<AppProps>) {
  const context = useMemo(
    () => createAppContext(props.enforcedLanguage),
    [props.enforcedLanguage]
  );

  return (
    <Suspense
      fallback={
        <Translate term="CommonTerms.LoadingPleaseWait" fallback="Loading..." />
      }
    >
      <UIProvider>
        <GlobalStyles />
        <CSSReset />
        <Fonts />
        <AppProvider
          services={context}
          style={{height: '100%'}}
          useExperimentalIntl
        >
          <QueryClientProvider client={context.queryClient}>
            {props.children}
            <ToastProvider>
              <ToastManager maxToasts={3} />
            </ToastProvider>
            <ReactQueryDevtools />
          </QueryClientProvider>
        </AppProvider>
      </UIProvider>
    </Suspense>
  );
}
